/**
 * Cost Modeling Workspace
 *
 * Admin UI for gold-standard cost modeling, actual-vs-modeled comparison,
 * and what-if scenario analysis across all LLM calls.
 *
 * Three sections:
 *   1. Gold Standard Model — per-tier cost breakdown by category
 *   2. Actual vs Modeled — real spend vs projections with delta highlighting
 *   3. Scenario Builder — create/calculate hypothetical model swaps
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Calculator,
  BarChart3,
  X,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { API_BASE_URL } from '../../utils/apiConfig';
import { storage } from '../../services/storage';
import { AdminDateRangePicker } from './AdminDateRangePicker';
import { useAdminDateRange } from '../../hooks/useAdminDateRange';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tier = 'trial' | 'full' | 'extended';
type Section = 'gold' | 'actual' | 'scenarios';

/**
 * Internal tier key -> public tier name. `full` is CC Chat+ and `extended` is
 * CC Pro; `basic` (not served by this endpoint) is CC Essentials. Getting this
 * wrong mislabels which cohort a cost belongs to, which is the one thing a
 * cost screen cannot afford to do.
 */
const TIER_LABELS: Record<string, string> = {
  trial: 'Trial',
  basic: 'Essentials',
  full: 'Chat+',
  extended: 'Pro',
};

/**
 * Per-row honesty flags (Aug 6 2026, WS6). The server sends numeric 0.0 for
 * anything it could not price so this FE's arithmetic stays safe; the flags —
 * not the number — say whether that 0 is a price or a gap. A row carrying any
 * of them renders "—".
 */
interface PricingFlags {
  /** The 0.0 is a placeholder, not a price. `unpriced_reason` says why. */
  unpriced?: boolean;
  /**
   * Server-side exclusion code (routers/admin_cost_modeling.py, sourced from
   * the COST_EXCLUDED_* constants in services/llm_profile_promotion.py).
   */
  unpriced_reason?: string | null;
  /** Registry has no token estimates — nothing to multiply the price by. */
  has_token_estimates?: boolean;
  /** No frequency for this tier — the monthly figure has no multiplier. */
  frequency_missing?: boolean;
}

/** Matches COST_EXCLUDED_PER_MINUTE_BILLED. */
const REASON_PER_MINUTE_BILLED = 'per_minute_billed_model';

/** Response-level coverage counts that make a total a floor rather than a sum. */
interface FloorMeta {
  pricing_basis?: string;
  unpriced_call_count?: number;
  calls_without_estimates?: number;
  calls_missing_frequency?: number;
  totals_are_floor?: boolean;
  /** Actual-vs-modeled names it this way: only the modeled column can be a floor. */
  modeled_totals_are_floor?: boolean;
}

/**
 * True when a row's dollar figures are real. Every flag is optional because an
 * older server states none of them; absent means "not stated", which is not the
 * same as false — only an explicit gap suppresses the number.
 */
function isPriced(flags: PricingFlags | undefined | null): boolean {
  if (!flags) return true;
  if (flags.unpriced === true) return false;
  if (flags.has_token_estimates === false) return false;
  return true;
}

interface TierCost extends PricingFlags {
  frequency_per_week: number;
  monthly_cost: number;
}

interface GoldCall extends PricingFlags {
  call_name: string;
  display_name: string;
  category: string;
  model: string;
  cost_per_call: number;
  estimated_input_tokens?: number;
  estimated_output_tokens?: number;
  tier_costs: {
    trial: TierCost;
    full: TierCost;
    extended: TierCost;
  };
}

interface GoldData extends FloorMeta {
  calls: GoldCall[];
  category_totals: Record<string, Record<string, number>>;
  grand_totals: Record<string, number>;
  total_active_calls: number;
}

interface ActualComparison extends PricingFlags {
  call_name: string;
  display_name?: string;
  category?: string;
  /**
   * This endpoint names the gap `modeled_frequency_missing` (the actual side
   * has its own measured frequency, so only the modeled column can be missing
   * one). Same meaning as `frequency_missing` elsewhere.
   */
  modeled_frequency_missing?: boolean;
  modeled_cost_per_call: number;
  actual_avg_cost_per_call: number;
  modeled_monthly_cost: number;
  actual_monthly_cost: number;
  delta_pct: number;
  has_actual_data: boolean;
}

interface ActualData extends FloorMeta {
  comparisons: ActualComparison[];
  totals: {
    modeled_monthly_per_user: number;
    actual_monthly_per_user: number;
    delta_pct: number;
  };
}

interface Scenario {
  id: number;
  name: string;
  description?: string;
  overrides?: Record<string, string>;
  created_at?: string;
}

/**
 * Per-call rows key the pair `current_monthly` / `scenario_monthly`; the
 * response's grand_totals key the same pair `current` / `scenario`. Both are
 * declared so neither reader has to trust the other endpoint's naming — and so
 * a missing key surfaces as "—" rather than a render crash.
 */
interface ScenarioTierComparison {
  current_monthly?: number;
  scenario_monthly?: number;
  current?: number;
  scenario?: number;
  delta: number;
  delta_pct: number;
}

function scenarioCurrent(comp: ScenarioTierComparison): number | undefined {
  return comp.current_monthly ?? comp.current;
}

function scenarioProjected(comp: ScenarioTierComparison): number | undefined {
  return comp.scenario_monthly ?? comp.scenario;
}

interface ScenarioCall extends PricingFlags {
  call_name: string;
  display_name?: string;
  current_model: string;
  scenario_model: string;
  is_overridden: boolean;
  tier_comparison: {
    trial?: ScenarioTierComparison;
    full?: ScenarioTierComparison;
    extended?: ScenarioTierComparison;
  };
}

interface ScenarioResult extends FloorMeta {
  scenario: { name: string };
  calls: ScenarioCall[];
  grand_totals: Record<string, ScenarioTierComparison>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * A dollar figure, or "—" when there is no figure.
 *
 * null/undefined/NaN reach here whenever a field is absent from the response —
 * a key rename, an older server, a total the backend declined to state. The
 * old version called .toFixed() on it and threw, taking the whole section
 * down; printing "$0.00" instead would be worse still, because a fabricated
 * zero reads as a real, cheap price.
 */
function fmt(n: number | null | undefined, decimals = 4): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n === 0) return '$0.00';
  if (Math.abs(n) < 0.01) return `$${n.toFixed(decimals)}`;
  return `$${n.toFixed(2)}`;
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n === 0) return '0%';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

/** A row's dollar cell: the number when it is real, "—" (with a why) when not. */
function moneyCell(value: number | null | undefined, flags: PricingFlags | undefined, decimals = 4) {
  if (isPriced(flags)) return <>{fmt(value, decimals)}</>;
  const reason = flags?.unpriced_reason === REASON_PER_MINUTE_BILLED
    ? 'billed per audio-minute — token arithmetic not meaningful'
    : flags?.has_token_estimates === false
      ? 'no token estimate in the registry — not priced, not free'
      : 'no price for this model — not priced, not free';
  return <span style={{ color: '#64748b' }} title={reason}>—</span>;
}

/**
 * Totals that omit unpriceable calls are floors, not sums — those calls
 * contributed $0 to the number above. Says so, with the counts, or renders
 * nothing when the server states no gap.
 */
function FloorNote({ meta, subject }: { meta: FloorMeta | null | undefined; subject: string }) {
  if (!meta) return null;
  const isFloor = meta.totals_are_floor === true || meta.modeled_totals_are_floor === true;
  if (!isFloor) return null;

  const parts: string[] = [];
  if (meta.unpriced_call_count) parts.push(`${meta.unpriced_call_count} unpriced`);
  if (meta.calls_without_estimates) parts.push(`${meta.calls_without_estimates} without token estimates`);
  if (meta.calls_missing_frequency) parts.push(`${meta.calls_missing_frequency} without a frequency`);

  return (
    <div style={{
      background: '#422006',
      border: '1px solid #854d0e',
      borderRadius: 6,
      padding: '8px 12px',
      marginBottom: 12,
      fontSize: 12,
      color: '#fcd34d',
    }}>
      <strong>Floor, not a total.</strong> {subject} exclude{' '}
      {parts.length > 0 ? parts.join(', ') : 'calls that could not be priced'} — those
      contribute $0 here, so the real figure is at least this.
      {meta.pricing_basis ? <> Basis: {meta.pricing_basis}.</> : null}
    </div>
  );
}

function deltaColor(pct: number): string {
  if (pct < -2) return '#4ade80'; // green — saving money
  if (pct > 2) return '#f87171';  // red — over budget
  return '#94a3b8';               // neutral
}

async function getToken(): Promise<string> {
  const token = await storage.getItem('admin_token');
  if (!token) throw new Error('Admin token not found. Please log in again.');
  return token;
}

async function apiFetch(path: string, opts: RequestInit = {}): Promise<any> {
  const token = await getToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s = {
  wrapper: {
    color: '#e2e8f0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  } as React.CSSProperties,
  sectionTabs: {
    display: 'flex',
    gap: 4,
    marginBottom: 20,
    borderBottom: '1px solid #334155',
    paddingBottom: 0,
  } as React.CSSProperties,
  sectionTab: (active: boolean) => ({
    padding: '10px 20px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderBottom: active ? '2px solid #60a5fa' : '2px solid transparent',
    color: active ? '#60a5fa' : '#94a3b8',
    fontWeight: active ? 600 : 400,
    fontSize: 14,
    transition: 'all 0.15s',
  }) as React.CSSProperties,
  card: {
    background: '#16213e',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    border: '1px solid #1e3a5f',
  } as React.CSSProperties,
  summaryRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  summaryCard: {
    background: '#1e293b',
    borderRadius: 8,
    padding: '14px 20px',
    flex: '1 1 200px',
    border: '1px solid #334155',
  } as React.CSSProperties,
  summaryLabel: {
    fontSize: 11,
    color: '#94a3b8',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 4,
  } as React.CSSProperties,
  summaryValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#f1f5f9',
  } as React.CSSProperties,
  tierTabs: {
    display: 'flex',
    gap: 4,
    marginBottom: 16,
  } as React.CSSProperties,
  tierTab: (active: boolean) => ({
    padding: '6px 16px',
    cursor: 'pointer',
    background: active ? '#2563eb' : '#1e293b',
    border: active ? '1px solid #3b82f6' : '1px solid #334155',
    borderRadius: 6,
    color: active ? '#fff' : '#94a3b8',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
  }) as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 13,
  } as React.CSSProperties,
  th: {
    textAlign: 'left' as const,
    padding: '8px 10px',
    borderBottom: '1px solid #334155',
    color: '#94a3b8',
    fontWeight: 500,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  } as React.CSSProperties,
  thRight: {
    textAlign: 'right' as const,
    padding: '8px 10px',
    borderBottom: '1px solid #334155',
    color: '#94a3b8',
    fontWeight: 500,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  } as React.CSSProperties,
  td: {
    padding: '7px 10px',
    borderBottom: '1px solid #1e293b',
    color: '#e2e8f0',
  } as React.CSSProperties,
  tdRight: {
    padding: '7px 10px',
    borderBottom: '1px solid #1e293b',
    color: '#e2e8f0',
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
  categoryRow: {
    background: '#1e293b',
    cursor: 'pointer',
  } as React.CSSProperties,
  categoryCell: {
    padding: '8px 10px',
    fontWeight: 600,
    color: '#60a5fa',
    fontSize: 13,
    borderBottom: '1px solid #334155',
  } as React.CSSProperties,
  totalRow: {
    background: '#0f172a',
    fontWeight: 700,
  } as React.CSSProperties,
  btn: (variant: 'primary' | 'secondary' | 'danger' = 'primary') => ({
    padding: '7px 14px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: variant === 'primary' ? '#2563eb' : variant === 'danger' ? '#dc2626' : '#334155',
    color: '#fff',
    transition: 'opacity 0.15s',
  }) as React.CSSProperties,
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  } as React.CSSProperties,
  modalContent: {
    background: '#1a1a2e',
    borderRadius: 12,
    padding: 24,
    width: 440,
    maxWidth: '90vw',
    border: '1px solid #334155',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 6,
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#e2e8f0',
    fontSize: 14,
    outline: 'none',
    marginBottom: 12,
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  muted: {
    color: '#64748b',
    fontSize: 12,
  } as React.CSSProperties,
  noData: {
    opacity: 0.4,
  } as React.CSSProperties,
  badge: (color: string) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    background: color,
  }) as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 40, justifyContent: 'center', color: '#94a3b8' }}>
      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
      Loading...
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={{ ...s.card, borderColor: '#dc2626', display: 'flex', alignItems: 'center', gap: 10 }}>
      <AlertTriangle size={18} color="#f87171" />
      <span style={{ flex: 1, color: '#f87171' }}>{message}</span>
      {onRetry && (
        <button style={s.btn('secondary')} onClick={onRetry}>
          <RefreshCw size={14} /> Retry
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 1: Gold Standard Model
// ---------------------------------------------------------------------------

function GoldStandardSection() {
  const [tier, setTier] = useState<Tier>('full');
  const [data, setData] = useState<GoldData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await apiFetch(`/api/admin/cost-modeling/gold-standard?tier=${tier}`);
      setData(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tier]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!data) return null;

  // Group calls by category
  const grouped: Record<string, GoldCall[]> = {};
  for (const call of data.calls) {
    const cat = call.category || 'Uncategorized';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(call);
  }
  const categories = Object.keys(grouped).sort();

  const tierLabel = TIER_LABELS;
  const grandTotal = data.grand_totals?.[tier];

  return (
    <div>
      {/* Tier tabs */}
      <div style={s.tierTabs}>
        {(['trial', 'full', 'extended'] as Tier[]).map(t => (
          <button key={t} style={s.tierTab(tier === t)} onClick={() => setTier(t)}>
            {tierLabel[t]}
          </button>
        ))}
      </div>

      {/* Summary */}
      <div style={s.summaryRow}>
        <div style={s.summaryCard}>
          <div style={s.summaryLabel}>Monthly Cost / User ({tierLabel[tier]})</div>
          <div style={s.summaryValue}>{fmt(grandTotal, 2)}</div>
        </div>
        <div style={s.summaryCard}>
          <div style={s.summaryLabel}>Active LLM Calls</div>
          <div style={s.summaryValue}>{data.total_active_calls}</div>
        </div>
      </div>

      <FloorNote meta={data} subject="These modeled totals" />

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Call</th>
              <th style={s.th}>Model</th>
              <th style={s.thRight}>Est. In Tokens</th>
              <th style={s.thRight}>Est. Out Tokens</th>
              <th style={s.thRight}>Freq/Week</th>
              <th style={s.thRight}>Cost/Call</th>
              <th style={s.thRight}>Monthly</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => {
              const calls = grouped[cat];
              const isCollapsed = collapsed[cat] ?? false;
              const catTotal = data.category_totals?.[cat]?.[tier] ?? calls.reduce(
                (sum, c) => sum + (c.tier_costs[tier]?.monthly_cost ?? 0), 0
              );
              return (
                <React.Fragment key={cat}>
                  {/* Category header */}
                  <tr
                    style={s.categoryRow}
                    onClick={() => setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }))}
                  >
                    <td style={s.categoryCell} colSpan={6}>
                      {isCollapsed ? <ChevronRight size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <ChevronDown size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                      {cat} ({calls.length})
                    </td>
                    <td style={{ ...s.categoryCell, textAlign: 'right' }}>{fmt(catTotal, 2)}</td>
                  </tr>
                  {/* Call rows */}
                  {!isCollapsed && calls.map(call => {
                    const tc = call.tier_costs[tier];
                    // The monthly figure inherits the per-call gap AND adds its
                    // own: a frequency the registry never carried.
                    const monthlyFlags: PricingFlags = {
                      unpriced: call.unpriced || tc?.unpriced,
                      unpriced_reason: call.unpriced_reason,
                      has_token_estimates: call.has_token_estimates,
                      frequency_missing: call.frequency_missing || tc?.frequency_missing,
                    };
                    const freqMissing = monthlyFlags.frequency_missing === true;
                    return (
                      <tr key={call.call_name}>
                        <td style={s.td}>{call.display_name || call.call_name}</td>
                        <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{call.model}</td>
                        <td style={s.tdRight}>{call.estimated_input_tokens?.toLocaleString() ?? '—'}</td>
                        <td style={s.tdRight}>{call.estimated_output_tokens?.toLocaleString() ?? '—'}</td>
                        <td style={s.tdRight}>
                          {freqMissing
                            ? <span style={{ color: '#64748b' }} title="no frequency for this tier in the registry">—</span>
                            : tc?.frequency_per_week ?? '—'}
                        </td>
                        <td style={s.tdRight}>{moneyCell(call.cost_per_call, call)}</td>
                        <td style={s.tdRight}>
                          {freqMissing
                            ? <span style={{ color: '#64748b' }} title="no frequency for this tier — monthly cost has no multiplier">—</span>
                            : moneyCell(tc?.monthly_cost, monthlyFlags, 2)}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {/* Grand total */}
            <tr style={s.totalRow}>
              <td style={{ ...s.td, fontWeight: 700 }} colSpan={6}>Grand Total ({tierLabel[tier]})</td>
              <td style={{ ...s.tdRight, fontWeight: 700, color: '#60a5fa' }}>{fmt(grandTotal, 2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2: Actual vs Modeled
// ---------------------------------------------------------------------------

function ActualVsModeledSection() {
  const { startDate, endDate, setRange, queryParams } = useAdminDateRange('30d');
  const [data, setData] = useState<ActualData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await apiFetch(`/api/admin/cost-modeling/actual-vs-modeled?${queryParams}`);
      setData(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!data) return null;

  // Sort by absolute delta descending
  const sorted = [...data.comparisons].sort((a, b) => Math.abs(b.delta_pct) - Math.abs(a.delta_pct));
  const totals = data.totals;

  return (
    <div>
      {/* Range selector */}
      <div style={{ marginBottom: 16 }}>
        <AdminDateRangePicker startDate={startDate} endDate={endDate} onChange={setRange} />
      </div>

      {/* Summary */}
      <div style={s.summaryRow}>
        <div style={s.summaryCard}>
          <div style={s.summaryLabel}>Modeled Monthly / User</div>
          <div style={s.summaryValue}>{fmt(totals.modeled_monthly_per_user, 2)}</div>
        </div>
        <div style={s.summaryCard}>
          <div style={s.summaryLabel}>Actual Monthly / User</div>
          <div style={s.summaryValue}>{fmt(totals.actual_monthly_per_user, 2)}</div>
        </div>
        <div style={s.summaryCard}>
          <div style={s.summaryLabel}>Overall Delta</div>
          <div style={{ ...s.summaryValue, color: deltaColor(totals.delta_pct) }}>
            {totals.delta_pct < 0 ? <TrendingDown size={20} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <TrendingUp size={20} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
            {fmtPct(totals.delta_pct)}
          </div>
        </div>
      </div>

      {/* The modeled column is the side with gaps — actuals are measured from
          the ledger either way, so a floor on modeled inflates the delta. */}
      <FloorNote meta={data} subject="The modeled column and its delta" />

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Call</th>
              <th style={s.th}>Category</th>
              <th style={s.thRight}>Modeled $/call</th>
              <th style={s.thRight}>Actual $/call</th>
              <th style={s.thRight}>Modeled $/mo</th>
              <th style={s.thRight}>Actual $/mo</th>
              <th style={s.thRight}>Delta</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => {
              const rowStyle = row.has_actual_data ? {} : s.noData;
              // A delta is a ratio against the modeled side. When that side is
              // an unpriced $0 the server sends delta_pct=0, which would read
              // as "spot on" — the one reading it definitely is not.
              const modeledMonthlyFlags: PricingFlags = {
                unpriced: row.unpriced,
                unpriced_reason: row.unpriced_reason,
                has_token_estimates: row.has_token_estimates,
                frequency_missing: row.modeled_frequency_missing,
              };
              // A modeled monthly needs a frequency as well as a price.
              const modeledReal = isPriced(row) && row.modeled_frequency_missing !== true;
              const deltaShown = row.has_actual_data && modeledReal;
              return (
                <tr key={row.call_name} style={rowStyle}>
                  <td style={s.td}>{row.display_name || row.call_name}</td>
                  <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{row.category || '—'}</td>
                  <td style={s.tdRight}>{moneyCell(row.modeled_cost_per_call, row)}</td>
                  <td style={s.tdRight}>{row.has_actual_data ? fmt(row.actual_avg_cost_per_call) : '—'}</td>
                  <td style={s.tdRight}>
                    {row.modeled_frequency_missing === true
                      ? <span style={{ color: '#64748b' }} title="no modeled frequency in the registry — monthly cost has no multiplier">—</span>
                      : moneyCell(row.modeled_monthly_cost, modeledMonthlyFlags, 2)}
                  </td>
                  <td style={s.tdRight}>{row.has_actual_data ? fmt(row.actual_monthly_cost, 2) : '—'}</td>
                  <td style={{ ...s.tdRight, color: deltaShown ? deltaColor(row.delta_pct) : '#64748b', fontWeight: 600 }}>
                    {deltaShown
                      ? fmtPct(row.delta_pct)
                      : !row.has_actual_data
                        ? 'No data'
                        : <span title="modeled side not priced — no baseline to compare against">Not modeled</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3: Scenario Builder
// ---------------------------------------------------------------------------

function ScenarioBuilderSection() {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Calculation results keyed by scenario id
  const [results, setResults] = useState<Record<number, ScenarioResult>>({});
  const [calculating, setCalculating] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showAllCalls, setShowAllCalls] = useState<Record<number, boolean>>({});

  const loadScenarios = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await apiFetch('/api/admin/cost-modeling/scenarios');
      setScenarios(d.scenarios || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  async function createScenario() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await apiFetch('/api/admin/cost-modeling/scenarios', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), description: newDesc.trim() }),
      });
      setShowNewModal(false);
      setNewName('');
      setNewDesc('');
      await loadScenarios();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function calculateScenario(id: number) {
    setCalculating(id);
    try {
      const d = await apiFetch(`/api/admin/cost-modeling/scenarios/${id}/calculate`);
      setResults(prev => ({ ...prev, [id]: d }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCalculating(null);
    }
  }

  async function deleteScenario(id: number) {
    setDeleting(id);
    try {
      await apiFetch(`/api/admin/cost-modeling/scenarios/${id}`, { method: 'DELETE' });
      setScenarios(prev => prev.filter(s => s.id !== id));
      setResults(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDeleting(null);
    }
  }

  if (loading) return <LoadingSpinner />;

  const tierLabel = TIER_LABELS;

  return (
    <div>
      {error && <ErrorBanner message={error} onRetry={loadScenarios} />}

      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: '#94a3b8', fontSize: 13 }}>
          {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''}
        </span>
        <button style={s.btn('primary')} onClick={() => setShowNewModal(true)}>
          <Plus size={14} /> New Scenario
        </button>
      </div>

      {/* Scenario list */}
      {scenarios.length === 0 && (
        <div style={{ ...s.card, textAlign: 'center', color: '#64748b', padding: 40 }}>
          No scenarios yet. Create one to start modeling.
        </div>
      )}
      {scenarios.map(sc => {
        const result = results[sc.id];
        const isCalc = calculating === sc.id;
        const isDel = deleting === sc.id;
        const showAll = showAllCalls[sc.id] ?? false;

        return (
          <div key={sc.id} style={s.card}>
            {/* Scenario header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{sc.name}</div>
                {sc.description && <div style={s.muted}>{sc.description}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={s.btn('primary')}
                  onClick={() => calculateScenario(sc.id)}
                  disabled={isCalc}
                >
                  {isCalc ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Calculator size={14} />}
                  {isCalc ? 'Calculating...' : 'Calculate'}
                </button>
                <button
                  style={s.btn('danger')}
                  onClick={() => deleteScenario(sc.id)}
                  disabled={isDel}
                >
                  <Trash2 size={14} /> {isDel ? '...' : 'Delete'}
                </button>
              </div>
            </div>

            {/* Results */}
            {result && (
              <div>
                {/* Grand totals per tier */}
                <div style={s.summaryRow}>
                  {Object.entries(result.grand_totals).map(([tierKey, comp]) => (
                    <div key={tierKey} style={s.summaryCard}>
                      <div style={s.summaryLabel}>{tierLabel[tierKey] || tierKey}</div>
                      <div style={{ display: 'flex', gap: 16, alignItems: 'baseline', marginTop: 4 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>Current</div>
                          <div style={{ fontSize: 18, fontWeight: 600 }}>{fmt(scenarioCurrent(comp), 2)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>Scenario</div>
                          <div style={{ fontSize: 18, fontWeight: 600 }}>{fmt(scenarioProjected(comp), 2)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>Savings</div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: deltaColor(-comp.delta_pct) }}>
                            {fmtPct(-comp.delta_pct)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <FloorNote meta={result} subject="These scenario totals" />

                {/* Overridden calls table */}
                {(() => {
                  const overridden = result.calls.filter(c => c.is_overridden);
                  const rest = result.calls.filter(c => !c.is_overridden);
                  const displayed = showAll ? result.calls : overridden;

                  if (overridden.length === 0 && !showAll) {
                    return (
                      <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 16 }}>
                        No model overrides in this scenario.
                      </div>
                    );
                  }

                  return (
                    <div>
                      <table style={s.table}>
                        <thead>
                          <tr>
                            <th style={s.th}>Call</th>
                            <th style={s.th}>Current Model</th>
                            <th style={s.th}>Scenario Model</th>
                            <th style={s.thRight}>Current $/mo</th>
                            <th style={s.thRight}>Scenario $/mo</th>
                            <th style={s.thRight}>Delta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayed.map(call => {
                            const comp = call.tier_comparison?.full || call.tier_comparison?.trial;
                            if (!comp) return null;
                            return (
                              <tr key={call.call_name} style={call.is_overridden ? {} : s.noData}>
                                <td style={s.td}>
                                  {call.display_name || call.call_name}
                                  {call.is_overridden && (
                                    <span style={{ ...s.badge('#2563eb'), marginLeft: 6 }}>override</span>
                                  )}
                                </td>
                                <td style={{ ...s.td, fontSize: 12, color: '#94a3b8' }}>{call.current_model}</td>
                                <td style={{ ...s.td, fontSize: 12, color: call.is_overridden ? '#60a5fa' : '#94a3b8' }}>
                                  {call.scenario_model}
                                </td>
                                <td style={s.tdRight}>{moneyCell(scenarioCurrent(comp), call, 2)}</td>
                                <td style={s.tdRight}>{moneyCell(scenarioProjected(comp), call, 2)}</td>
                                <td style={{ ...s.tdRight, color: isPriced(call) ? deltaColor(comp.delta_pct) : '#64748b', fontWeight: 600 }}>
                                  {isPriced(call)
                                    ? fmtPct(comp.delta_pct)
                                    : <span title="one side of this swap has no price — no delta to state">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {rest.length > 0 && (
                        <div style={{ textAlign: 'center', marginTop: 8 }}>
                          <button
                            style={{ ...s.btn('secondary'), fontSize: 12 }}
                            onClick={() => setShowAllCalls(prev => ({ ...prev, [sc.id]: !showAll }))}
                          >
                            {showAll ? `Hide unchanged (${rest.length})` : `Show all calls (${rest.length} unchanged)`}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })}

      {/* New Scenario Modal */}
      {showNewModal && (
        <div style={s.modal} onClick={() => setShowNewModal(false)}>
          <div style={s.modalContent} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>New Scenario</div>
              <button
                onClick={() => setShowNewModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              >
                <X size={18} />
              </button>
            </div>
            <label style={{ fontSize: 13, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Name</label>
            <input
              style={s.input}
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Switch chat to GPT-4o-mini"
              autoFocus
            />
            <label style={{ fontSize: 13, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Description (optional)</label>
            <input
              style={s.input}
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              placeholder="What-if analysis for..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button style={s.btn('secondary')} onClick={() => setShowNewModal(false)}>Cancel</button>
              <button style={s.btn('primary')} onClick={createScenario} disabled={creating || !newName.trim()}>
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export interface CostModelingWorkspaceProps {
  /** Optional: pre-select a section */
  initialSection?: Section;
}

export function CostModelingWorkspace({ initialSection = 'gold' }: CostModelingWorkspaceProps) {
  const [section, setSection] = useState<Section>(initialSection);

  const sections: { key: Section; label: string; icon: React.ReactNode }[] = [
    { key: 'gold', label: 'Gold Standard', icon: <DollarSign size={14} /> },
    { key: 'actual', label: 'Actual vs Modeled', icon: <BarChart3 size={14} /> },
    { key: 'scenarios', label: 'Scenario Builder', icon: <Calculator size={14} /> },
  ];

  return (
    <div style={s.wrapper}>
      {/* Section navigation */}
      <div style={s.sectionTabs}>
        {sections.map(sec => (
          <button
            key={sec.key}
            style={s.sectionTab(section === sec.key)}
            onClick={() => setSection(sec.key)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {sec.icon} {sec.label}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      {section === 'gold' && <GoldStandardSection />}
      {section === 'actual' && <ActualVsModeledSection />}
      {section === 'scenarios' && <ScenarioBuilderSection />}
    </div>
  );
}

export default CostModelingWorkspace;
